"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { ConversationProvider } from "@elevenlabs/react";
import { AgentHeader, ChatHistory, AgentFooter, NewSessionDialog } from "./layout";
import { AgentPanelAnimation, FloatingButtonAnimation } from "./animations";
import { IconButton } from "@/components/ui/buttons";
import { useAgentController } from "@/features/agent";
import type { AddMessageInput } from "@/types/agent/message";
import { getTranslations, type Locale } from "@/lib/i18n/config";
import { pushToDataLayer } from "@/lib/analytics/pushToDataLayer";
interface AgentProps {
  initialMessages?: AddMessageInput[];
  debug?: boolean;
  locale?: Locale;
}

/**
 * Consumes the ElevenLabs conversation. Must live inside `<ConversationProvider>`
 * (see `Agent` below), because `useAgentController` calls `useConversation`.
 */
const AgentInner: React.FC<AgentProps> = t0 => {
  const $ = _c(52);
  const {
    initialMessages,
    debug,
    locale: t1
  } = t0;
  const locale = t1 === undefined ? "de" : t1;
  let t2;
  if ($[0] !== locale) {
    t2 = getTranslations(locale);
    $[0] = locale;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    agent: t
  } = t2;
  let t3;
  if ($[2] !== t.initialMessage) {
    t3 = {
      type: "text",
      sender: "assistant",
      content: t.initialMessage
    };
    $[2] = t.initialMessage;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const defaultInitialMessage = t3;
  let t4;
  if ($[4] !== defaultInitialMessage || $[5] !== initialMessages) {
    t4 = initialMessages ?? [defaultInitialMessage];
    $[4] = defaultInitialMessage;
    $[5] = initialMessages;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== locale || $[8] !== t4) {
    t5 = {
      initialMessages: t4,
      locale
    };
    $[7] = locale;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    state,
    actions
  } = useAgentController(t5);
  let t6;
  if ($[10] !== actions) {
    t6 = () => {
      pushToDataLayer({
        event: "agent_new_session"
      });
      actions.startNewSession();
      actions.closeNewSessionDialog();
    };
    $[10] = actions;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleConfirmNewSession = t6;
  let t7;
  if ($[12] !== actions) {
    t7 = (message, files) => {
      if (!files || files.length === 0) {
        actions.sendUserMessage(message);
        return;
      }
      const normalizedMessage = message.trim();
      const attachmentData = {
        message: normalizedMessage
      };
      if (files.length === 1) {
        attachmentData.attachment = files[0];
      } else {
        attachmentData.attachment = files;
      }
      pushToDataLayer({
        event: "ask_agent"
      });
      actions.sendUserMessage(normalizedMessage, false, {
        formSubmission: {
          formId: "footer_attachment",
          data: attachmentData
        }
      });
    };
    $[12] = actions;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleSendMessage = t7;
  let t8;
  if ($[14] !== actions.close || $[15] !== actions.openNewSessionDialog || $[16] !== locale) {
    t8 = <AgentHeader onNewSession={actions.openNewSessionDialog} onClose={actions.close} locale={locale} />;
    $[14] = actions.close;
    $[15] = actions.openNewSessionDialog;
    $[16] = locale;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== actions.closeNewSessionDialog || $[19] !== actions.declineConsent || $[20] !== actions.expand || $[21] !== actions.grantConsent || $[22] !== actions.toggleVoice || $[23] !== debug || $[24] !== handleConfirmNewSession || $[25] !== handleSendMessage || $[26] !== locale || $[27] !== state.isCollapsed || $[28] !== state.isNewSessionDialogOpen || $[29] !== state.isVoice || $[30] !== state.messageHistory || $[31] !== state.needsConsent || $[32] !== state.statusStep) {
    t9 = state.isNewSessionDialogOpen ? <NewSessionDialog onConfirm={handleConfirmNewSession} onCancel={actions.closeNewSessionDialog} locale={locale} /> : <><ChatHistory messages={state.messageHistory.messages} debug={debug} statusStep={state.statusStep} locale={locale} isVoiceActive={state.isVoice} /><AgentFooter onSendMessage={handleSendMessage} shouldAutoFocus={true} locale={locale} isCollapsed={state.isCollapsed} onInputFocus={actions.expand} isVoiceActive={state.isVoice} onToggleVoice={actions.toggleVoice} consentRequired={state.needsConsent} onGrantConsent={actions.grantConsent} onDeclineConsent={actions.declineConsent} /></>;
    $[18] = actions.closeNewSessionDialog;
    $[19] = actions.declineConsent;
    $[20] = actions.expand;
    $[21] = actions.grantConsent;
    $[22] = actions.toggleVoice;
    $[23] = debug;
    $[24] = handleConfirmNewSession;
    $[25] = handleSendMessage;
    $[26] = locale;
    $[27] = state.isCollapsed;
    $[28] = state.isNewSessionDialogOpen;
    $[29] = state.isVoice;
    $[30] = state.messageHistory;
    $[31] = state.needsConsent;
    $[32] = state.statusStep;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== actions.expand || $[35] !== locale || $[36] !== state.isCollapsed || $[37] !== state.isOpen || $[38] !== t8 || $[39] !== t9) {
    t10 = <AgentPanelAnimation isVisible={state.isOpen} isCollapsed={state.isCollapsed} onExpand={actions.expand} locale={locale}>{t8}{t9}</AgentPanelAnimation>;
    $[34] = actions.expand;
    $[35] = locale;
    $[36] = state.isCollapsed;
    $[37] = state.isOpen;
    $[38] = t8;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  const t11 = !state.isOpen;
  let t12;
  if ($[41] !== actions) {
    t12 = () => {
      pushToDataLayer({
        event: "agent_start"
      });
      actions.open();
    };
    $[41] = actions;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== t.openAssistant || $[44] !== t12) {
    t13 = <IconButton icon="message-bubble" onClick={t12} color="yellow-purple-hover" ariaLabel={t.openAssistant} />;
    $[43] = t.openAssistant;
    $[44] = t12;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] !== t11 || $[47] !== t13) {
    t14 = <FloatingButtonAnimation isVisible={t11}>{t13}</FloatingButtonAnimation>;
    $[46] = t11;
    $[47] = t13;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] !== t10 || $[50] !== t14) {
    t15 = <>{t10}{t14}</>;
    $[49] = t10;
    $[50] = t14;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  return t15;
};
export const Agent: React.FC<AgentProps> = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <ConversationProvider><AgentInner {...props} /></ConversationProvider>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};